import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshControl, Modal, ScrollView, View } from 'react-native';
import PropTypes from 'prop-types';
import { useStore } from 'Store';
import Theme from 'Theme';
import { Background, RecordingList, ActivityIndicator, useRecordings } from 'LocalGluons';
import defaultStyles from 'Pages/Recordings/Recordings.jss';
import { defaultRecStorage } from 'Utils/itemUtils';
import { Device } from 'DeviceUtils';
import { ITEM_TYPES } from 'Utils';
import { DETAIL_HEADER_HEIGHT, TOP_BAR_TOTAL_HEIGHT } from '../../../Navigation/Menu/Menu.styles';
import { RECORDINGS_TYPE } from '../../../../options';
import EditSelectBar from '../components/EditSelectBar';
import BuyStorageEditButtonsBar from '../components/BuyStorageEditButtonsBar';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { useGetRealId } from '../utils';
import BuyMoreStorageDialog from '../components/BuyMoreStorageDialog';
import { useNavigationPropsContext } from '../../../Navigation/navigationPropsContext';

export const filterItems = (items, planned) => {
    const displayItems = items.filter((item) => {
        if (item.modelType === 'recording') return planned === item.isPlanned();
        return item.episodes.some((ep) => planned === ep.isPlanned());
    });
    displayItems.forEach((item) => {
        if (item.modelType === 'recordingSeries') {
            item.episodes = item.episodes.filter((ep) => ep.isPlanned() === planned);
        }
    });
    return displayItems;
};

// eslint-disable-next-line max-statements
const AllRecordings = ({ navigation, route }) => {
    const { appManager } = useStore();
    const api = appManager.getApi();

    const navWillFocusListener = useRef(null);
    const [isLoading, setLoading] = useState(true);
    const [isRefreshing, setRefreshing] = useState(false);
    const [usedSpace, setUsedSpace] = useState(100);
    const {
        plannedRecordings,
        updateRecordings,
        recordings,
        recordingsTimeGo,
        recordingsTimeIptv
    } = useRecordings();
    const [items, setItems] = useState([]);
    const [isEditing, setEditing] = useState(false);
    const [allCheckbox, setAllCheckbox] = useState(false);
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [showBuyStorageModal, setShowBuyStorageModal] = useState(false);
    const firstLoad = useRef(false);
    const selectedSeries = useRef([]);

    const recordingsType = route.params?.recordingsType || undefined;

    const isRecordingsIPTV = recordingsType === RECORDINGS_TYPE.IPTV_RECORDINGS;
    const isPlannedRecordings = recordingsType === RECORDINGS_TYPE.PLANNED_RECORDINGS;
    const isMyRecordings = recordingsType === RECORDINGS_TYPE.MY_RECORDINGS;
    const { navigate, updateNavigationProps } = useNavigationPropsContext();
    const parseMyRecordings = useCallback(
        () =>
            filterItems(
                recordings.filter(
                    (record) => record.data.status === 'done' && record.data.platform === 'go'
                ),
                false
            ),
        [recordings]
    );

    const parseIPTVRecordings = useCallback(
        () =>
            recordings.filter(
                (record) => record.data.status === 'done' && record.data.platform === 'iptv'
            ),
        [recordings]
    );

    const parseData = () => {
        firstLoad.current = true;
        let recordingsResult = [];

        if (isRecordingsIPTV) recordingsResult = parseIPTVRecordings();
        if (isMyRecordings) recordingsResult = parseMyRecordings();
        if (isPlannedRecordings) recordingsResult = plannedRecordings;

        if (!isPlannedRecordings) {
            const { recordingTimeToUsed, recordingTimeTotal } = isRecordingsIPTV
                ? recordingsTimeIptv
                : recordingsTimeGo;
            const unclampedPercentage = (recordingTimeToUsed / recordingTimeTotal) * 100;
            const percentageTaken = Math.min(100, Math.max(0, unclampedPercentage));
            setUsedSpace(percentageTaken);
        }

        setItems(recordingsResult);
        setLoading(false);
        setRefreshing(false);
    };

    const buyMoreData = useCallback(() => {
        setShowBuyStorageModal(true);
    }, []);
    const closeBuyStorageDialog = useCallback(() => {
        setShowBuyStorageModal(false);
    }, []);

    const getRealId = useGetRealId();

    const selectAll = useCallback(() => {
        if (!allCheckbox) {
            const allIds = items.reduce((res, curr) => {
                if (curr.modelType === 'recordingSeries') {
                    return [...res, ...curr.episodes.map((ep) => getRealId(ep))];
                }
                return [...res, getRealId(curr)];
            }, []);
            setSelectedItemIds(allIds);
        } else {
            setSelectedItemIds([]);
            selectedSeries.current = [];
        }
        setAllCheckbox(!allCheckbox);
    }, [items, allCheckbox]);

    const startEditing = useCallback(() => setEditing(true), []);

    const stopEditing = useCallback(() => {
        setEditing(false);
        setAllCheckbox(false);
        setSelectedItemIds([]);
    }, []);

    const onRefresh = useCallback(async () => {
        setRefreshing(!firstLoad);
        setLoading(true);
        await updateRecordings();
    }, [firstLoad]);

    useEffect(() => {
        const unsubscribe = navigation.addListener('blur', () => {
            onRefresh();
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        setLoading(true);
        parseData();
    }, [recordings, plannedRecordings]);

    const onDeleteConfirm = useCallback(async () => {
        setEditing(false);
        if (selectedSeries.current.length > 0) {
            selectedSeries.current.forEach((seriesId) => {
                api.backstageConnector.tv.stopSeriesRecording({
                    seriesId,
                    storage: defaultRecStorage
                });
            });
        }
        await api.backstageConnector.tv.deleteRecording({
            recordingIds: selectedItemIds.join(',')
        });

        setShowDeleteConfirmModal(false);
        setLoading(true);
        await updateRecordings();
        setAllCheckbox(false);
        setSelectedItemIds([]);
    }, [selectedItemIds]);

    const isAfterCurrentTime = (endTimeUTC) => {
        if (!endTimeUTC) return false;
        return endTimeUTC > Date.now();
    };

    const routeToDetail = useCallback((selectedItem) => {
        const id = selectedItem?.getId();
        const type = selectedItem?.modelType;
        const platform = selectedItem?.data?.pckg?.platform;
        const endTimeUTC = selectedItem?.data?.schedule?.endTimeUTC;

        //check if item in recordings is still in live and if is, behave in navigation as live item not recording
        let navigateParams;
        if (type !== ITEM_TYPES.RECORDING_SERIES) {
            navigateParams = {
                id,
                type: isAfterCurrentTime(endTimeUTC) ? ITEM_TYPES.TV : type,
                platform
            };
        } else {
            navigateParams = { id, type, platform };
        }

        navigate('Details', { item: selectedItem }, navigateParams);
    }, []);

    const onDelete = useCallback(() => {
        setShowDeleteConfirmModal(true);
    }, []);

    const closeDeleteConfirmDialog = useCallback(() => {
        setShowDeleteConfirmModal(false);
    }, []);

    const onGroupCheckboxSelect = (ids, groupId) => {
        if (ids.length > 0 && !selectedSeries.current.includes(groupId)) {
            selectedSeries.current.push(groupId);
            return;
        }
        selectedSeries.current = selectedSeries.current.filter((id) => id !== groupId);
    };

    useEffect(() => {
        parseData();
        navWillFocusListener.current = navigation.addListener('focus', () => {
            parseData();
        });

        return () => {
            navWillFocusListener.current();
        };
    }, []);

    useEffect(() => {
        const totalItemCount = items.reduce((res, curr) => {
            if (curr.modelType === 'recordingSeries') {
                return res + curr.episodes.length;
            }
            return res + 1;
        }, 0);
        if (allCheckbox && selectedItemIds.length !== totalItemCount) {
            setAllCheckbox(false);
        } else if (!allCheckbox && selectedItemIds.length === totalItemCount) {
            setAllCheckbox(true);
        }
    }, [items, selectedItemIds, allCheckbox]);

    return (
        <Background>
            <View
                style={[
                    defaultStyles.viewAllContainer,
                    Device.isWeb
                        ? {
                              paddingTop: TOP_BAR_TOTAL_HEIGHT + 2 * DETAIL_HEADER_HEIGHT,
                              transition: 'margin .16s linear'
                          }
                        : {}
                ]}
            >
                <Modal
                    animationType="slide"
                    transparent
                    visible={showDeleteConfirmModal}
                    onRequestClose={closeDeleteConfirmDialog}
                >
                    <DeleteConfirmDialog
                        close={closeDeleteConfirmDialog}
                        onDeleteConfirm={onDeleteConfirm}
                    />
                </Modal>
                <Modal
                    animationType="slide"
                    transparent
                    visible={showBuyStorageModal}
                    onRequestClose={closeBuyStorageDialog}
                >
                    <BuyMoreStorageDialog close={closeBuyStorageDialog} />
                </Modal>
                {items.length > 0 && (
                    <EditSelectBar
                        editing={isEditing}
                        loading={isLoading}
                        refreshing={isRefreshing}
                        allCheckbox={allCheckbox}
                        selectAll={selectAll}
                        startEditing={startEditing}
                    />
                )}
                <ScrollView
                    refreshControl={
                        <RefreshControl
                            colors={[Theme.primaryActiveColor]}
                            tintColor={Theme.primaryActiveColor}
                            refreshing={isRefreshing}
                            onRefresh={onRefresh}
                        />
                    }
                >
                    <View>
                        {!isLoading && (
                            <RecordingList
                                items={items}
                                isLoading={isLoading}
                                onSelect={routeToDetail}
                                editedIds={selectedItemIds}
                                setEditedIds={setSelectedItemIds}
                                isEditing={isEditing}
                                getRealId={getRealId}
                                planned={isPlannedRecordings}
                                onGroupCheckboxSelect={
                                    isPlannedRecordings ? onGroupCheckboxSelect : undefined
                                }
                                navigation={navigation}
                            />
                        )}
                    </View>
                </ScrollView>
                {isLoading && <ActivityIndicator color={Theme.primaryActiveColor} size="large" />}
                <BuyStorageEditButtonsBar
                    editedIds={selectedItemIds}
                    usedSpace={!isPlannedRecordings ? usedSpace : undefined}
                    isEditing={isEditing}
                    stopEditing={stopEditing}
                    deleteSelected={onDelete}
                    buyMoreData={isRecordingsIPTV ? buyMoreData : undefined}
                />
            </View>
        </Background>
    );
};

AllRecordings.propTypes = {
    navigation: PropTypes.shape({}).isRequired
};

export default AllRecordings;
